"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  getAlgoliaSearchCredentials,
  getAlgoliaSearchTargetByIndex,
} from "@/lib/algolia";
import {
  clearPendingConversion,
  configureAlgoliaInsightsCredentials,
  peekPendingConversion,
  sendSearchResultConverted,
} from "@/lib/algolia-insights";

/** 체류 시간 게이트 — 검색 결과를 클릭해 도달한 페이지에 이만큼 머물면 "전환"으로 인정. */
const DWELL_MS = 3000;

/**
 * 검색 결과 클릭 → 목적지 도달 시 Algolia 전환 이벤트를 전송한다 (JCG-2181).
 *
 * Search.tsx가 결과 클릭 시 sessionStorage에 보존한 컨텍스트(queryID/objectID/userToken)를
 * 다음 경로 진입 때 읽어 `convertedObjectIDsAfterSearch`로 전송한다. queryID가 검색→클릭→전환을
 * 하나로 묶어, 이후 A/B·Recommend·재랭킹의 데이터 연료가 된다.
 *
 * 체류 게이트: 보존된 목적지 경로(path)에 도달해 DWELL_MS(3초) 이상 머문 경우에만 전환 전송.
 * - 경로 매칭: 클릭 시 보존한 path와 현재 pathname이 같을 때만 동작 → 엉뚱한 페이지 전환 방지.
 * - peek 후 dwell 만료 시점에 clear+전송 → strict mode effect 이중 호출에도 전환 1회 보장.
 * - 일찍 이탈하면 cleanup이 타이머만 취소(보존 데이터 유지) → 같은 검색 페이지 재진입 시 재시도 가능,
 *   미사용분은 TTL(5분) 또는 다음 검색 클릭으로 정리된다.
 *
 * LayoutContent globals에 마운트돼 셸/클래식 모든 분기에서 상주한다(UI 없음, null 반환).
 */
export default function SearchInsightsConversionTracker() {
  const pathname = usePathname();

  useEffect(() => {
    const pending = peekPendingConversion();
    // 보존된 목적지 경로에 도달했을 때만 전환 대상으로 인정.
    if (!pending || pending.path !== pathname) return;

    const { index, queryID, objectID, userToken, authenticatedUserToken } =
      pending;
    const target = getAlgoliaSearchTargetByIndex(index);
    if (!target) return;

    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const credentials = await getAlgoliaSearchCredentials(target);
          if (cancelled) return;
          configureAlgoliaInsightsCredentials({
            appId: credentials.appId,
            apiKey: credentials.apiKey,
          });
          clearPendingConversion(); // dwell 충족 시점에 소비 → 1회만 전송
          sendSearchResultConverted(
            { index, queryID, objectID },
            { userToken, authenticatedUserToken }
          );
        } catch {
          // 텔레메트리 실패는 검색/네비게이션 흐름에 영향 없음
        }
      })();
    }, DWELL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pathname]);

  return null;
}
